import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type ThumbStatus = 'queued' | 'generating' | 'generated' | 'ready' | 'error';

export interface ThumbItem {
  id: string;
  taleId: string;
  idx: number;
  variant: 'A' | 'B' | 'C' | 'D';
  title: string;
  subtitle: string;
  palette?: string | { bg: string; fg: string; accent: string } | null;
  style?: string | null;
  r2_key_svg?: string | null;
  r2_key_png?: string | null;
  status: ThumbStatus;
  isDefault?: number | boolean;
  created_at?: number;
  updated_at?: number;
}

type ThumbDict = Record<string, ThumbItem>;

const API = {
  list: (taleId: string) => `/api/thumbnails/tale/${taleId}`,
  // Use the existing images stream that emits both image.status and thumb.status
  sse:  (taleId: string) => `/api/images/stream?taleId=${encodeURIComponent(taleId)}`,
};

function mergeThumb(prev: ThumbItem | undefined, patch: Partial<ThumbItem>): ThumbItem {
  return {
    ...(prev as ThumbItem),
    ...(patch as ThumbItem),
  };
}

/**
 * Live thumbnail updater:
 * - initial fetch from /api/thumbnails/:taleId
 * - SSE subscribe to /api/images/stream?taleId (expects `thumb.status` events)
 * - auto polling fallback every 4s when SSE isn't available
 */
export function useThumbUpdates(taleId: string) {
  const [thumbs, setThumbs] = useState<ThumbDict>({});
  const [connected, setConnected] = useState<boolean>(false);
  const pollTimer = useRef<number | null>(null);
  const esRef = useRef<EventSource | null>(null);

  const listUrl = useMemo(() => API.list(taleId), [taleId]);
  const sseUrl  = useMemo(() => API.sse(taleId), [taleId]);

  const installPolling = useCallback(() => {
    if (pollTimer.current) return;
    // Poll light—just enough to keep cards moving
    pollTimer.current = window.setInterval(async () => {
      try {
        const r = await fetch(listUrl, { credentials: 'include' });
        if (!r.ok) return;
        const j = await r.json();
        const items: ThumbItem[] = (j?.items ?? []).map((x: any) => normalizeThumb(x));
        setThumbs(prev => {
          const next = { ...prev };
          for (const it of items) next[it.id] = mergeThumb(prev[it.id], it);
          return next;
        });
      } catch {}
    }, 4000);
  }, [listUrl]);

  const uninstallPolling = useCallback(() => {
    if (pollTimer.current) {
      window.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const refresh = useCallback(async () => {
    const r = await fetch(listUrl, { credentials: 'include' });
    if (!r.ok) throw new Error(`thumbs_list_failed ${r.status}`);
    const j = await r.json();
    const items: ThumbItem[] = (j?.items ?? []).map((x: any) => normalizeThumb(x));
    const next: ThumbDict = {};
    for (const it of items) next[it.id] = it;
    setThumbs(next);
    return items.length;
  }, [listUrl]);

  useEffect(() => {
    let cancelled = false;

    // Initial snapshot
    refresh().catch(() => { /* ignore */ });

    // Try SSE first
    const es = new EventSource(sseUrl, { withCredentials: true });
    esRef.current = es;

    es.onopen = () => {
      setConnected(true);
      uninstallPolling(); // Prefer SSE
    };

    es.onerror = () => {
      setConnected(false);
      // Fall back to polling if SSE drops
      installPolling();
      // Let the browser reconnect EventSource automatically.
    };

    // Server sends:
    //   event: update
    //   data: { thumbId, taleId, status, r2KeyPng?, ... }
    const onUpdate = (ev: MessageEvent) => {
      try {
        const data = JSON.parse(ev.data);
        // Handle thumb events by id (with fallback to thumbId and imageId)
        if (!data) return;
        const id = data.id ?? data.thumbId ?? data.imageId;
        if (!id) return;

        setThumbs(prev => {
          const patch: Partial<ThumbItem> = {
            id,
            taleId: data.taleId,
            status: data.status || 'ready', // don't default to "error"
          };

          if (data.r2KeyPng) patch.r2_key_png = data.r2KeyPng;
          if (data.r2KeySvg) patch.r2_key_svg = data.r2KeySvg;
          if (typeof data.idx === 'number') patch.idx = data.idx;
          if (data.variant) patch.variant = data.variant;
          if (data.title) patch.title = data.title;
          if (data.subtitle) patch.subtitle = data.subtitle;
          if (data.palette) patch.palette = data.palette;
          if (typeof data.isDefault !== 'undefined') patch.isDefault = data.isDefault;

          return { ...prev, [id]: mergeThumb(prev[id], patch) };
        });
      } catch {
        // ignore malformed events
      }
    };

    es.addEventListener('update', onUpdate);

    return () => {
      cancelled = true;
      setConnected(false);
      es.removeEventListener('update', onUpdate);
      es.close();
      esRef.current = null;
      uninstallPolling();
    };
  }, [sseUrl, refresh, installPolling, uninstallPolling]);

  const list = useMemo(() => {
    return Object.values(thumbs).sort((a, b) => (a.idx - b.idx) || a.variant.localeCompare(b.variant));
  }, [thumbs]);

  // A convenience helper to optimistically patch one thumb in-place
  const patch = useCallback((id: string, partial: Partial<ThumbItem>) => {
    setThumbs(prev => ({ ...prev, [id]: mergeThumb(prev[id], partial) }));
  }, []);

  return { thumbs: list, byId: thumbs, connected, refresh, patch };
}

// --- helpers ----
function normalizeThumb(x: any): ThumbItem {
  // Map API payload to our shape; keep both snake_case & camelCase keys resiliently
  return {
    id: x.id,
    taleId: x.taleId ?? x.tale_id,
    idx: Number(x.idx ?? 0),
    variant: (x.variant || 'A') as any,
    title: x.title ?? '',
    subtitle: x.subtitle ?? '',
    palette: x.palette,
    style: x.style,
    r2_key_svg: x.r2_key_svg ?? x.r2KeySvg ?? null,
    r2_key_png: x.r2_key_png ?? x.r2KeyPng ?? null,
    status: (x.status ?? 'ready') as ThumbStatus,
    isDefault: !!(x.isDefault ?? x.is_default),
    created_at: x.created_at ?? x.createdAt,
    updated_at: x.updated_at ?? x.updatedAt,
  };
}
